import type { VeivoTheme } from "@veivo/web-sdk";

/**
 * Maps the operator's branding onto the Veivo SDK's Tier 1 theme tokens, so the
 * capture flow looks like the portal it is embedded in rather than like a
 * third-party widget dropped into it.
 *
 * Driven by the OPERATOR's branding, not RideMinder's, because the portal is
 * white-label: an operator with their own colours would otherwise see RideMinder
 * purple appear the moment verification started.
 *
 * Only `primary` is branded: the flow's buttons (Start walk-around, Yes it
 * matches, Take photo…) and the active step. `accent` is deliberately NOT: in the
 * walk-around camera (HUD v2, DEV-1806) it is the success colour — the locked
 * framing guide, the hold countdown, the "✓ Front captured" toast, the turntable
 * arc and the angle bars — and success stays the SDK's green on every operator.
 * Those chips and the toast also carry dark text and icons drawn for that light
 * green; a dark brand colour behind them would be unreadable. `warn` and `danger`
 * are not themed either: amber and red carry meaning a brand palette would
 * undermine.
 */
export const veivoThemeFromBranding = (branding?: {
  primary?: string;
  dark?: string;
  light?: string;
}): VeivoTheme => ({
  ...(branding?.primary ? { primary: branding.primary } : {}),
  // Matches the portal's own card radius so the embedded flow does not look
  // bolted on.
  radius: "8px",
  // Inherit the app's font stack rather than naming one, so a font change in
  // the portal carries into the flow automatically.
  font: "inherit",
});

/** A hex colour as the QR link carries it: 3 or 6 hex digits, no "#". */
const LINK_COLOUR = /^[0-9a-f]{3}([0-9a-f]{3})?$/i;

/** The operator's primary colour for the QR link (see veivoHandoffUrl): its
 *  hex digits, or null when it is not a plain hex colour. */
export const brandForLink = (primary?: string): string | null => {
  const digits = primary?.trim().replace(/^#/, "") ?? "";
  return LINK_COLOUR.test(digits) ? digits : null;
};

/**
 * The operator's primary colour back from the QR link, on the public phone page
 * (which has no login, so no operator to ask). The link is the user's to edit,
 * so only a plain hex colour is taken — nothing else can reach the SDK's styles.
 */
export const brandFromLink = (value: string | null): { primary: string } | undefined => {
  const digits = value?.trim() ?? "";
  return LINK_COLOUR.test(digits) ? { primary: `#${digits}` } : undefined;
};
